import { useState, useMemo, useEffect } from "react";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import MontureCard from "./MontureCard";
import { useMontures } from "@/context/MonturesContext";

const PER_PAGE = 8;

const FILTERS = [
    { id: "tous", label: "Tous" },
    { id: "femme", label: "Femme" },
    { id: "homme", label: "Homme" },
    { id: "enfant", label: "Enfant" },
];

function matchFilter(m, filter) {
    if (filter === "tous") return true;
    // Lunettes enfant : taille spécifique → on n'inclut pas le "mixte" adulte
    if (filter === "enfant") return m.genre === "enfant";
    return m.genre === filter || m.genre === "mixte";
}

export default function StockGrid({ onSelect }) {
    const { items: allMontures } = useMontures();
    const [page, setPage] = useState(1);
    const [filter, setFilter] = useState("tous");

    const filtered = useMemo(
        () => allMontures.filter((m) => matchFilter(m, filter)),
        [allMontures, filter]
    );

    const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
    const visible = useMemo(
        () => filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE),
        [filtered, page]
    );

    // Reset page on filter change
    useEffect(() => {
        setPage(1);
    }, [filter]);

    const goTo = (p) => {
        setPage(Math.min(Math.max(1, p), totalPages));
        if (typeof window !== "undefined") {
            const el = document.getElementById("stock");
            if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    };

    return (
        <section
            id="stock"
            className="relative py-24 md:py-32 bg-lpl-bg"
            data-testid="stock-section"
        >
            <div className="max-w-7xl mx-auto px-5 md:px-10">
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-10">
                    <div>
                        <p className="font-serif italic text-2xl md:text-3xl text-lpl-terracotta mb-3">
                            Notre sélection
                        </p>
                        <h2
                            className="font-display font-semibold text-5xl md:text-7xl text-lpl-text leading-[0.95] tracking-tight"
                            data-testid="stock-title"
                        >
                            Toutes nos{" "}
                            <span className="font-serif italic font-normal text-lpl-primary-deep">
                                montures
                            </span>
                            <span className="font-serif italic font-normal text-lpl-lavender">
                                .
                            </span>
                        </h2>
                    </div>
                    <p className="md:max-w-sm text-lpl-text-muted text-base md:text-lg leading-relaxed">
                        {allMontures.length} montures sélectionnées avec amour
                        chez{" "}
                        <span className="text-lpl-text font-semibold">
                            La Petite Lunetterie
                        </span>
                        . Cliquez pour découvrir.
                    </p>
                </div>

                {/* Filters */}
                <div
                    className="flex items-center flex-wrap gap-2 md:gap-3 mb-10 pb-6 border-b border-lpl-border"
                    data-testid="stock-filters"
                >
                    <span className="text-xs uppercase tracking-[0.18em] font-semibold text-lpl-text-muted mr-2">
                        Filtrer
                    </span>
                    {FILTERS.map((f) => {
                        const active = filter === f.id;
                        const count = allMontures.filter((m) =>
                            matchFilter(m, f.id)
                        ).length;
                        return (
                            <button
                                key={f.id}
                                type="button"
                                onClick={() => setFilter(f.id)}
                                data-testid={`filter-${f.id}`}
                                aria-pressed={active}
                                className={`px-5 py-2.5 rounded-full font-body text-sm font-medium transition-colors border-2 ${
                                    active
                                        ? "bg-lpl-primary text-lpl-text border-lpl-text"
                                        : "bg-transparent text-lpl-text border-lpl-border hover:border-lpl-text"
                                }`}
                            >
                                {f.label}{" "}
                                <span className="opacity-60 text-xs ml-1">
                                    {count}
                                </span>
                            </button>
                        );
                    })}
                </div>

                {filtered.length === 0 ? (
                    <p className="text-center py-20 text-lpl-text-muted text-lg">
                        Aucune monture pour ce filtre.
                    </p>
                ) : (
                    <motion.div
                        key={`${filter}-${page}`}
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.45 }}
                        className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5"
                    >
                        {visible.map((m) => (
                            <MontureCard
                                key={m.id}
                                monture={m}
                                onClick={() => onSelect && onSelect(m)}
                                showLimitedBadge
                            />
                        ))}
                    </motion.div>
                )}

                {totalPages > 1 && (
                    <div
                        className="mt-14 flex items-center justify-center gap-2"
                        data-testid="pagination"
                    >
                        <button
                            type="button"
                            onClick={() => goTo(page - 1)}
                            disabled={page === 1}
                            aria-label="Page précédente"
                            data-testid="pagination-prev"
                            className="w-11 h-11 rounded-full border-2 border-lpl-text text-lpl-text hover:bg-lpl-text hover:text-lpl-bg transition-colors flex items-center justify-center disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-lpl-text"
                        >
                            <ChevronLeft size={18} />
                        </button>
                        {Array.from({ length: totalPages }).map((_, i) => {
                            const p = i + 1;
                            const active = p === page;
                            return (
                                <button
                                    key={p}
                                    type="button"
                                    onClick={() => goTo(p)}
                                    aria-current={active ? "page" : undefined}
                                    data-testid={`pagination-page-${p}`}
                                    className={`w-11 h-11 rounded-full font-display font-semibold text-base transition-colors flex items-center justify-center ${
                                        active
                                            ? "bg-lpl-primary text-lpl-text"
                                            : "text-lpl-text hover:bg-lpl-bg-alt"
                                    }`}
                                >
                                    {p}
                                </button>
                            );
                        })}
                        <button
                            type="button"
                            onClick={() => goTo(page + 1)}
                            disabled={page === totalPages}
                            aria-label="Page suivante"
                            data-testid="pagination-next"
                            className="w-11 h-11 rounded-full border-2 border-lpl-text text-lpl-text hover:bg-lpl-text hover:text-lpl-bg transition-colors flex items-center justify-center disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-lpl-text"
                        >
                            <ChevronRight size={18} />
                        </button>
                    </div>
                )}
            </div>
        </section>
    );
}
